import { useState, useMemo } from 'react';
import { Form, useForm } from "@repo/ui/components/form";
import { TextField } from "@repo/ui/components/text-field";
import { Button } from "@repo/ui/components/button";
import SectionChild from "~/components/SectionChild";
import BreadcrumbPageCus from "~/components/breadcrumb-page";
import Hero from "~/components/hero";
import { MapPin, Phone, Info } from "@repo/ui/components/icon";
import { useTranslation } from "react-i18next";
import SectionTitle from './__components/section-title';
import { Alert, AlertDescription } from '@repo/ui/components/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@repo/ui/components/dialog';
import { z } from 'zod/v4';
import { zodResolver } from '@hookform/resolvers/zod';
import { clientCallAPI } from '~/services/clientCallAPI';
import { buildCanonicalLink, buildMetaFromLoader, buildSeoMeta, getOrigin } from '~/utils/seo';
import { hostName } from '~/utils/format';
import { Route } from './+types/lien-he';
import { serverCallApiCMS } from '~/services/serverCallAPI';
import { IPage } from '~/types/types';
import { useLoaderData } from 'react-router';
import { StructuredDataScript } from '~/components/structured-data-script';
export async function loader({ request }: Route.LoaderArgs) {
  const contentPage = await serverCallApiCMS<IPage>(
    "/api/v1/web/pages/gioi-thieu"
  ).catch(() => {
    return {
      slug: "",
      description: "",
      content: "",
      seo: {
        title: "Liên hệ",
        meta_description: "",
        focus_keyphrase: "",
        og_image: [],
      },
      structured: null,
      structuredData: null,
    };
  });

  const origin = getOrigin(request);
  const url = new URL(request.url);
  const canonical = origin ? `${origin}${url.pathname}` : url.pathname;

  return {
    seo: {
      canonical,
      ...contentPage.seo,
    },
    structuredData: contentPage.structuredData ?? null,
  };
}
export const handle = {
  dynamicLinks: (props: any) => {
    return buildCanonicalLink(props?.data?.seo?.canonical || "");
  },
};



export const meta = (args: Route.MetaArgs) => {
  return buildMetaFromLoader(
    args,
    {
      hostName,
      buildSeoMeta,
    },
    (ld) => ld
  );
};
function ContactInfo() {
  const { t } = useTranslation("contact");
  return (
    <div className="space-y-5">
      <SectionTitle align="left">{t("intro.headline")}</SectionTitle>
      <p className="">{t("intro.description")}</p>
      <div>
        <div className="space-y-2.5 max-w-[351px]">
          <div>
            <div className="font-semibold">{t("intro.office1.label")}</div>
            <div className="flex gap-2 mt-1 text-muted-foreground">
              <MapPin size={16} className="mt-0.5 min-w-4 text-primary" />
              <span>{t("intro.office1.address")}</span>
            </div>
          </div>
          <div>
            <div className="font-semibold">{t("intro.office2.label")}</div>
            <div className="flex gap-2 mt-1 text-muted-foreground">
              <MapPin size={16} className="mt-0.5 min-w-4 text-primary" />
              <span>{t("intro.office2.address")}</span>
            </div>
          </div>
          <div className="font-semibold">{t("intro.contact.label")}</div>
          <div className="flex items-center gap-2">
            <Phone size={18} className="min-w-4 text-primary" />
            <span>{t("intro.contact.phone")}</span>
          </div>
          <div className="flex items-center gap-2">
            <Info size={18} className="min-w-4 text-primary" />
            <span>{t("intro.contact.email")}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function ContactForm() {
  const { t } = useTranslation("contact");
  const schema = useMemo(
    () =>
      z.object({
        name: z
          .string(t("error.required", { ns: "common" }))
          .min(1, t("error.required", { ns: "common" }) as string)
          .max(100, t("error.max", { ns: "common", max: 100 }) as string),
        phone: z
          .string(t("error.required", { ns: "common" }))
          .min(1, t("error.required", { ns: "common" }) as string)
          .max(11, t("error.max", { ns: "common", max: 11 }) as string)
          .regex(/^\d+$/, t("error.invalid_phone", { ns: "common" }) as string),
        email: z
          .email(t("error.invalid_email", { ns: "common" }) as string),
        message: z.string().optional().or(z.literal("")),
      }),
    [t]
  );

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: { name: '', phone: '', email: '', message: '' },
    mode: 'onChange',
  });

  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [successOpen, setSuccessOpen] = useState(false);

  const onSubmit = async (data: any) => {
    setErrorMsg(null);
    try {
      await clientCallAPI('/api/consultation-form', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          FullName: data.name,
          Phone: data.phone,
          Email: data.email,
          Note: data.message || null,
        }),
      });
      setSuccessMsg('Gửi thông tin thành công. Chúng tôi sẽ liên hệ sớm nhất!');
      setSuccessOpen(true);
      form.reset();
    } catch (error) {
      setErrorMsg('Gửi không thành công. Vui lòng thử lại.');
    }
  };

  const handleCloseModal = (value: boolean) => {
    setSuccessOpen(value);
    if (!value) {
      setSuccessMsg(null);
    }
  };
  return (
    <div className="w-full max-w-xl p-6 ml-auto bg-primary/10 rounded-2xl md:p-8">
      <SectionTitle align="left">{t("form.title")}</SectionTitle>
      <Form {...form}>

        <form
          id="contact-form"
          className="grid gap-4 mt-5"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <TextField
            name="name"
            label={t("form.fields.name.label")}
            placeholder={t("form.fields.name.placeholder")}
          />
          <div className="grid gap-4 md:grid-cols-2">
            <TextField
              name="email"
              label={t("form.fields.email.label")}
              placeholder={t("form.fields.email.placeholder")}
            />
            <TextField
              name="phone"
              label={t("form.fields.phone.label")}
              placeholder={t("form.fields.phone.placeholder")}
            />
          </div>
          <TextField
            name="message"
            label={t("form.fields.message.label")}
            placeholder={t("form.fields.message.placeholder")}
          />
          <Button type="submit">{t("form.submit")}</Button>
          {errorMsg ? (
            <Alert variant="destructive">
              <AlertDescription>{errorMsg}</AlertDescription>
            </Alert>
          ) : null}
        </form>
      </Form>
      {successMsg ? (
        <Dialog open={successOpen} onOpenChange={handleCloseModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Thông báo</DialogTitle>
            </DialogHeader>
            <Alert className="border-0 bg-primary-50 text-primary">
              <AlertDescription>{successMsg}</AlertDescription>
            </Alert>
            <div className="flex justify-end">
              <Button onClick={() => handleCloseModal(false)}>Đóng</Button>
            </div>
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  );
}

export default function ContactPage() {
  const { t } = useTranslation("contact");
  const { structuredData } = useLoaderData<typeof loader>();
  return (
    <>
      <StructuredDataScript data={structuredData} />
      <Hero image="/image/about/hero.png" title={t("hero.title")} className="max-h-[450px]" />
      <SectionChild>
        <BreadcrumbPageCus />
        <div className="grid gap-8 py-6 md:grid-cols-2">
          <ContactInfo />
          <ContactForm />
        </div>
        <div className="py-24">
          <iframe
            title="map"
            src="https://www.google.com/maps?q=Duong+Dinh+Nghe,+Hanoi&output=embed"
            className="w-full h-[360px] md:h-[460px] rounded-2xl"
          />
        </div>
      </SectionChild>
    </>
  );
}
